import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Flag } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const REASONS = [
    { key: "harassment", label: "Harassment or bullying" },
    { key: "inappropriate", label: "Inappropriate / sexual content" },
    { key: "fake_profile", label: "Fake profile or catfishing" },
    { key: "scam", label: "Scam or fraud" },
    { key: "underage", label: "Seems underage" },
    { key: "other", label: "Something else" },
];

export default function ReportModal({ open, onClose, reportedUserId, reportedName, matchId }) {
    const [reason, setReason] = useState("");
    const [details, setDetails] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const submit = async () => {
        if (!reason) {
            toast.error("Pick a reason");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/reports", { reported_user_id: reportedUserId, reason, details, match_id: matchId });
            toast.success("Report sent — our team will review it. Thanks for keeping Flatch safe 💜");
            setReason("");
            setDetails("");
            onClose?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't send report");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    data-testid="report-modal"
                    className="fixed inset-0 z-[80] bg-background/80 backdrop-blur-xl p-4 grid place-items-end sm:place-items-center"
                >
                    <motion.div
                        initial={{ y: "100%", opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: "100%", opacity: 0 }}
                        transition={{ type: "spring", stiffness: 260, damping: 30 }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-md rounded-4xl border-2 border-foreground bg-card p-5 pb-6"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <div className="text-[10px] font-black uppercase tracking-widest text-destructive flex items-center gap-1">
                                    <Flag className="h-3 w-3" strokeWidth={3} /> report
                                </div>
                                <div className="font-display text-2xl font-black leading-tight">
                                    Report {reportedName || "this person"}
                                </div>
                            </div>
                            <button onClick={onClose} data-testid="report-close-btn" className="h-9 w-9 rounded-full hover:bg-accent grid place-items-center">
                                <X className="h-5 w-5" strokeWidth={2.5} />
                            </button>
                        </div>

                        <div className="space-y-2">
                            {REASONS.map((r) => (
                                <button
                                    key={r.key}
                                    onClick={() => setReason(r.key)}
                                    data-testid={`report-reason-${r.key}`}
                                    className={`w-full text-left text-sm rounded-2xl border px-4 py-3 transition-colors ${
                                        reason === r.key ? "border-primary bg-primary/10 font-semibold" : "border-border hover:bg-accent/50"
                                    }`}
                                >
                                    {r.label}
                                </button>
                            ))}
                        </div>

                        <textarea
                            value={details}
                            onChange={(e) => setDetails(e.target.value)}
                            data-testid="report-details-input"
                            placeholder="Add details (optional)"
                            rows={3}
                            className="mt-3 w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-primary resize-none"
                        />

                        <Button
                            onClick={submit}
                            disabled={submitting}
                            data-testid="report-submit-btn"
                            className="mt-4 h-12 w-full rounded-2xl font-black bg-destructive text-white hover:bg-destructive/90 lift"
                        >
                            {submitting ? "Sending…" : "Submit report"}
                        </Button>
                        <p className="text-[11px] text-muted-foreground mt-2 text-center">
                            Reports are anonymous. We review every one.
                        </p>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
